import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Tag,
  Progress,
  Typography,
  Tooltip,
  Modal,
  message,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
} from 'antd';
import {
  VideoCameraOutlined,
  SoundOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  ClearOutlined,
  PlayCircleOutlined,
  UnorderedListOutlined,
  DownloadOutlined,
  EyeOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import llmService from '../../services/llmService';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// 任务类型映射
const typeMap = {
  'MUSIC': { color: 'default', icon: <SoundOutlined />, text: '生成音乐' },
  'LYRICS': { color: 'pink', icon: <FileTextOutlined />, text: '生成歌词' },
  'generate': { color: 'blue', icon: <VideoCameraOutlined />, text: '图生视频' },
  'text_generate': { color: 'blue', icon: <VideoCameraOutlined />, text: '文生视频' },
  'textGenerate': { color: 'blue', icon: <VideoCameraOutlined />, text: '文生视频' },
  'first_tail_generate': { color: 'blue', icon: <VideoCameraOutlined />, text: '首尾生视频' },
  'reference_generate': { color: 'blue', icon: <VideoCameraOutlined />, text: '参照生视频' },
};

// 平台映射
const platformMap = {
  'suno': { color: 'green', icon: <SoundOutlined />, text: 'Suno' },
  '1': { color: 'blue', icon: <VideoCameraOutlined />, text: 'OpenAI' },
  '40': { color: 'purple', icon: <VideoCameraOutlined />, text: 'Kling' },
  '41': { color: 'cyan', icon: <VideoCameraOutlined />, text: 'Luma' },
  '42': { color: 'orange', icon: <VideoCameraOutlined />, text: 'Runway' },
  '43': { color: 'blue', icon: <VideoCameraOutlined />, text: 'CogVideo' },
  '44': { color: 'gold', icon: <VideoCameraOutlined />, text: 'Pika' },
  '45': { color: 'lime', icon: <VideoCameraOutlined />, text: 'Minimax' },
};

// 状态映射
const statusMap = {
  'SUCCESS': { color: 'green', icon: <CheckCircleOutlined />, text: '成功' },
  'NOT_START': { color: 'default', icon: <PauseCircleOutlined />, text: '未启动' },
  'SUBMITTED': { color: 'gold', icon: <ClockCircleOutlined />, text: '队列中' },
  'IN_PROGRESS': { color: 'blue', icon: <PlayCircleOutlined />, text: '执行中' },
  'FAILURE': { color: 'red', icon: <CloseCircleOutlined />, text: '失败' },
  'QUEUED': { color: 'orange', icon: <UnorderedListOutlined />, text: '排队中' },
  'UNKNOWN': { color: 'default', icon: <QuestionCircleOutlined />, text: '未知' },
  '': { color: 'default', icon: <LoadingOutlined />, text: '正在提交' },
};

const TaskLogs = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });
  const [contentModalVisible, setContentModalVisible] = useState(false);
  const [currentContent, setCurrentContent] = useState('');
  const [previewModal, setPreviewModal] = useState({ visible: false, type: null, url: null, songs: null });
  const [filters, setFilters] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [playingSongId, setPlayingSongId] = useState(null);
  const [videoBlobUrl, setVideoBlobUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [llmToken, setLlmToken] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchLogs();
    fetchLlmToken();
  }, [pagination.page, pagination.page_size]);

  const fetchLlmToken = async () => {
    try {
      const response = await llmService.getTokens({ page: 1, page_size: 50 });
      if (response.success) {
        const tokens = response.data?.items || [];
        const enabled = tokens.filter(t => t.status === 1);
        enabled.sort((a, b) => {
          if (a.group === 'default') return -1;
          if (b.group === 'default') return 1;
          return 0;
        });
        if (enabled.length > 0) setLlmToken(enabled[0].key);
      }
    } catch {}
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
        ...filters,
      };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_timestamp = dateRange[0].unix();
        params.end_timestamp = dateRange[1].unix();
      }
      const response = await llmService.getTaskLogs(params);
      if (response.success) {
        setLogs(response.data?.items || []);
        setPagination(prev => ({
          ...prev,
          total: response.data?.total || 0,
        }));
      } else {
        message.error(response.error || t('Failed to retrieve logs'));
      }
    } catch (error) {
      message.error(t('Failed to retrieve logs'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const handleReset = () => {
    setFilters({});
    setDateRange(null);
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const renderType = (action) => {
    const type = typeMap[action] || { color: 'default', icon: <QuestionCircleOutlined />, text: action || '未知' };
    return <Tag color={type.color} icon={type.icon}>{t(type.text)}</Tag>;
  };

  const renderPlatform = (platform) => {
    const p = platformMap[platform] || platformMap[String(platform)] || { color: 'default', icon: <QuestionCircleOutlined />, text: platform || '未知' };
    return <Tag color={p.color} icon={p.icon}>{t(p.text)}</Tag>;
  };

  const renderStatus = (status) => {
    const s = statusMap[status] || statusMap['UNKNOWN'];
    return <Tag color={s.color} icon={s.icon}>{t(s.text)}</Tag>;
  };

  const renderDuration = (record) => {
    if (!record.submit_time || !record.finish_time) return '-';
    const durationSec = record.finish_time - record.submit_time;
    const color = durationSec > 60 ? 'red' : 'green';
    return <Tag color={color} icon={<ClockCircleOutlined />}>{t('{{durationSec}} seconds', { durationSec })}</Tag>;
  };

  const renderProgress = (progress, record) => {
    if (!progress) return '-';
    if (isNaN(progress.replace('%', ''))) {
      return <Text>{progress}</Text>;
    }
    const percent = parseInt(progress.replace('%', ''));
    const strokeColor = record.status === 'FAILURE' ? '#faad14' : undefined;
    return (
      <Progress
        percent={percent}
        size="small"
        strokeColor={strokeColor}
        style={{ minWidth: 120 }}
      />
    );
  };

  const openContentModal = (content) => {
    setCurrentContent(content);
    setContentModalVisible(true);
  };

  // 获取模型名称
  const getModelName = (record) => {
    return record.properties?.origin_model_name || record.properties?.upstream_model_name || '';
  };

  // 判断是否是视频任务
  const isVideoTask = (record) => {
    return ['generate', 'text_generate', 'textGenerate', 'first_tail_generate', 'reference_generate'].includes(record.action);
  };

  // 判断是否是音乐任务
  const isMusicTask = (record) => {
    return record.platform === 'suno' || ['MUSIC', 'LYRICS'].includes(record.action);
  };

  // 通过 llm.yeap.cloud 直接获取视频并预览
  const fetchAndPreviewVideo = async (taskId) => {
    if (!llmToken) { message.error(t('Please create an API key first')); return; }
    setVideoLoading(true);
    setPreviewModal({ visible: true, type: 'video', url: null, songs: null });
    try {
      const response = await axios.get(`https://llm.yeap.cloud/v1/videos/${taskId}/content`, {
        headers: { Authorization: `Bearer ${llmToken}` },
        responseType: 'blob',
        timeout: 120000,
      });
      const blobUrl = URL.createObjectURL(response.data);
      setVideoBlobUrl(blobUrl);
      setPreviewModal({ visible: true, type: 'video', url: blobUrl, songs: null });
    } catch (e) {
      message.error(t('Failed to load video'));
      setPreviewModal({ visible: false, type: null, url: null, songs: null });
    } finally {
      setVideoLoading(false);
    }
  };

  // 通过 llm.yeap.cloud 直接下载视频
  const downloadVideo = async (taskId) => {
    if (!llmToken) { message.error(t('Please create an API key first')); return; }
    try {
      const response = await axios.get(`https://llm.yeap.cloud/v1/videos/${taskId}/content`, {
        headers: { Authorization: `Bearer ${llmToken}` },
        responseType: 'blob',
        timeout: 120000,
      });
      const blobUrl = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `video-${taskId}.mp4`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      message.error(t('Failed to download video'));
    }
  };

  // 解析Suno歌曲
  const parseSongs = (data) => {
    if (!data) return [];
    let parsed = data;
    if (typeof data === 'string') {
      try { parsed = JSON.parse(data); } catch { return []; }
    }
    if (Array.isArray(parsed)) return parsed.filter(s => s.audio_url);
    if (parsed.audio_url) return [parsed];
    return [];
  };

  const handlePlaySong = (url, id) => {
    if (playingSongId === id) {
      audioRef.current?.pause();
      setPlayingSongId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(url);
    audio.play();
    audio.onended = () => setPlayingSongId(null);
    audioRef.current = audio;
    setPlayingSongId(id);
  };

  // 渲染结果列
  const renderResult = (_, record) => {
    const isSuccess = record.status === 'SUCCESS';

    if (isSuccess && isVideoTask(record) && record.result_url) {
      return (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />}
            onClick={() => fetchAndPreviewVideo(record.task_id)}>
            {t('Preview')}
          </Button>
          <Button type="link" size="small" icon={<DownloadOutlined />}
            onClick={() => downloadVideo(record.task_id)}>
            {t('Download')}
          </Button>
        </Space>
      );
    }

    if (isSuccess && isMusicTask(record) && record.data) {
      const songs = parseSongs(record.data);
      if (songs.length > 0) {
        return (
          <Button type="link" size="small" icon={<CustomerServiceOutlined />}
            onClick={() => setPreviewModal({ visible: true, type: 'music', url: null, songs })}>
            {t('Play ({{count}} tracks)', { count: songs.length })}
          </Button>
        );
      }
    }

    if (record.fail_reason) {
      return (
        <Tooltip title={record.fail_reason}>
          <Text type="danger" style={{ cursor: 'pointer', fontSize: 12 }}
            onClick={() => openContentModal(record.fail_reason)}>
            {record.fail_reason.length > 15 ? record.fail_reason.substring(0, 15) + '...' : record.fail_reason}
          </Text>
        </Tooltip>
      );
    }

    return <Text type="secondary">-</Text>;
  };

  const columns = [
    {
      title: t('Submitted at'),
      dataIndex: 'submit_time',
      key: 'submit_time',
      width: 160,
      render: (time) => time ? dayjs(time * 1000).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: t('Model'),
      key: 'model',
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        const model = getModelName(record);
        return model ? <Text code style={{ fontSize: 11 }}>{model}</Text> : '-';
      },
    },
    {
      title: t('Platform'),
      dataIndex: 'platform',
      key: 'platform',
      width: 90,
      render: renderPlatform,
    },
    {
      title: t('Type'),
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: renderType,
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: renderStatus,
    },
    {
      title: t('Progress'),
      dataIndex: 'progress',
      key: 'progress',
      width: 130,
      render: (progress, record) => renderProgress(progress, record),
    },
    {
      title: t('Time taken'),
      key: 'duration',
      width: 90,
      render: (_, record) => renderDuration(record),
    },
    {
      title: t('Task ID'),
      dataIndex: 'task_id',
      key: 'task_id',
      width: 180,
      ellipsis: true,
      render: (text, record) => (
        <Text
          copyable={{ text }}
          style={{ fontSize: 11, cursor: 'pointer', color: '#1890ff' }}
          onClick={() => openContentModal(JSON.stringify(record, null, 2))}
        >
          {text}
        </Text>
      ),
    },
    {
      title: t('Result'),
      key: 'result',
      width: 140,
      fixed: 'right',
      render: renderResult,
    },
  ];

  return (
    <div>
      <Card
        title={<><VideoCameraOutlined /> {t('Task logs (video/music generation)')}</>}
        extra={
          <Space wrap>
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              value={dateRange}
              onChange={setDateRange}
              placeholder={[t('Start time'), t('End time')]}
              style={{ width: 340 }}
            />
            <Input
              placeholder={t('Task ID')}
              value={filters.task_id || ''}
              onChange={e => setFilters({ ...filters, task_id: e.target.value })}
              style={{ width: 150 }}
              allowClear
            />
            <Select
              placeholder={t('Status')}
              value={filters.status}
              onChange={value => setFilters({ ...filters, status: value })}
              style={{ width: 100 }}
              allowClear
            >
              <Select.Option value="SUCCESS">{t('Success')}</Select.Option>
              <Select.Option value="IN_PROGRESS">{t('Running')}</Select.Option>
              <Select.Option value="FAILURE">{t('Failed')}</Select.Option>
              <Select.Option value="QUEUED">{t('Queued')}</Select.Option>
            </Select>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>{t('Search')}</Button>
            <Button icon={<ClearOutlined />} onClick={handleReset}>{t('Reset')}</Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={logs}
          rowKey={(record, index) => `${record.task_id || record.id || index}`}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.page_size,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('{{total}} total', { total }),
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, page, page_size: pageSize });
            },
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* 内容详情弹窗 */}
      <Modal
        title={t('Full details')}
        open={contentModalVisible}
        onCancel={() => setContentModalVisible(false)}
        footer={[
          <Button key="copy" onClick={() => {
            navigator.clipboard.writeText(currentContent);
            message.success(t('Copied'));
          }}>{t('Copy')}</Button>,
          <Button key="close" type="primary" onClick={() => setContentModalVisible(false)}>{t('Close')}</Button>,
        ]}
        width={700}
      >
        <div style={{
          background: '#f5f5f5', padding: 16, borderRadius: 4,
          maxHeight: 500, overflow: 'auto', whiteSpace: 'pre-wrap',
          wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12,
        }}>
          {currentContent}
        </div>
      </Modal>

      {/* 视频预览弹窗 */}
      <Modal
        title={t('Video preview')}
        open={previewModal.visible && previewModal.type === 'video'}
        onCancel={() => {
          if (videoBlobUrl) { URL.revokeObjectURL(videoBlobUrl); setVideoBlobUrl(null); }
          setPreviewModal({ visible: false, type: null, url: null, songs: null });
        }}
        footer={null}
        width={800}
        centered
        destroyOnClose
      >
        <div style={{ textAlign: 'center' }}>
          {videoLoading ? (
            <div style={{ padding: 60 }}>
              <LoadingOutlined style={{ fontSize: 32 }} />
              <div style={{ marginTop: 12 }}>{t('Loading video...')}</div>
            </div>
          ) : previewModal.url ? (
            <video
              src={previewModal.url}
              controls
              autoPlay
              style={{ maxWidth: '100%', maxHeight: '70vh' }}
            />
          ) : null}
        </div>
      </Modal>

      {/* 音乐预览弹窗 */}
      <Modal
        title={t('Music preview')}
        open={previewModal.visible && previewModal.type === 'music'}
        onCancel={() => {
          setPreviewModal({ visible: false, type: null, url: null, songs: null });
          if (audioRef.current) { audioRef.current.pause(); setPlayingSongId(null); }
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {(previewModal.songs || []).map((song, idx) => (
            <Card key={song.id || idx} size="small">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {song.image_url && (
                  <img src={song.image_url} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong ellipsis>{song.title || t('Track {{index}}', { index: idx + 1 })}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {song.duration ? `${Math.round(song.duration)}s` : ''} {song.model_name || ''}
                  </Text>
                </div>
                <Space>
                  <Button
                    type={playingSongId === song.id ? 'primary' : 'default'}
                    shape="circle"
                    size="small"
                    icon={playingSongId === song.id ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                    onClick={() => handlePlaySong(song.audio_url, song.id)}
                  />
                  <a href={song.audio_url} download target="_blank" rel="noreferrer">
                    <Button shape="circle" size="small" icon={<DownloadOutlined />} />
                  </a>
                </Space>
              </div>
            </Card>
          ))}
        </Space>
      </Modal>
    </div>
  );
};

export default TaskLogs;
